import React, { useContext, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { BreadcrumbContext } from '@/contexts/BreadcrumbContext';
import { NavbarToolbarContext } from '@/contexts/NavbarToolbarContext';
import { libraryBreadcrumbSegments } from '@/features/library/libraryTaxonomy';
import {
  ASK_PATH,
  DESK_PATH,
  LIBRARY_PATH,
  OPINIONS_PATH,
  askSessionPath,
  isAskPath,
  isDeskPath,
  isLibraryPath,
  isOpinionsPath,
  libraryDocumentPath,
} from '@/constants/appRoutes';
import { cn } from '@/lib/utils';

const COMPACT_MAX_WIDTH = 1120;

type BreadcrumbSegment = { label: string; path: string; title?: string };

const maxLabelLen = 35;
const truncate = (raw: string) =>
  raw.length > maxLabelLen ? raw.slice(0, maxLabelLen - 1).trim() + '…' : raw;

function useShellBreadcrumbs(): BreadcrumbSegment[] {
  const location = useLocation();
  const breadcrumbCtx = useContext(BreadcrumbContext);
  const pathname = location.pathname;
  const customLabel = breadcrumbCtx?.breadcrumbLabel ?? null;
  const suffix = breadcrumbCtx?.breadcrumbSuffix ?? null;

  if (isLibraryPath(pathname)) {
    const librarySegments = libraryBreadcrumbSegments(pathname);
    if (librarySegments) {
      return librarySegments.map((seg) => ({
        ...seg,
        label: truncate(seg.label),
        title: seg.label,
      }));
    }
  }

  if (pathname.startsWith(`${LIBRARY_PATH}/documents/`)) {
    const id = pathname.slice(`${LIBRARY_PATH}/documents/`.length);
    const raw = customLabel || 'Document';
    return [
      { label: 'RCM Library', path: LIBRARY_PATH },
      { label: truncate(raw), path: libraryDocumentPath(id), title: raw },
    ];
  }

  if (pathname === `${LIBRARY_PATH}/search`) {
    return [
      { label: 'RCM Library', path: LIBRARY_PATH },
      { label: 'Search', path: `${LIBRARY_PATH}/search` + (location.search ?? '') },
    ];
  }

  if (pathname.startsWith(`${LIBRARY_PATH}/questions/`)) {
    const raw = customLabel || 'Question';
    return [
      { label: 'RCM Library', path: LIBRARY_PATH },
      { label: truncate(raw), path: pathname, title: raw },
    ];
  }

  if (pathname.startsWith(`${LIBRARY_PATH}/`)) {
    const slug = pathname.slice(`${LIBRARY_PATH}/`.length);
    const raw = customLabel || slug.replace(/-/g, ' ');
    const segments: BreadcrumbSegment[] = [
      { label: 'RCM Library', path: LIBRARY_PATH },
      { label: truncate(raw), path: `${LIBRARY_PATH}/${slug}`, title: raw },
    ];
    if (suffix) {
      segments.push({
        label: truncate(suffix),
        path: pathname + (location.search ?? ''),
        title: suffix,
      });
    }
    return segments;
  }

  if (pathname === ASK_PATH) {
    return [];
  }

  if (isAskPath(pathname) && pathname !== ASK_PATH) {
    const id = pathname.slice(`${ASK_PATH}/`.length).split('/')[0];
    const raw = customLabel || 'Conversation';
    return [
      { label: 'Ask RCM', path: ASK_PATH },
      { label: truncate(raw), path: askSessionPath(id), title: raw },
    ];
  }

  if (pathname === '/projects') {
    const segments: BreadcrumbSegment[] = [
      { label: 'My Projects', path: '/projects' },
      {
        label: truncate(customLabel || 'All resources'),
        path: '/projects',
        title: customLabel || 'All resources',
      },
    ];
    if (suffix) {
      segments.push({
        label: truncate(suffix),
        path: pathname + (location.search ?? ''),
        title: suffix,
      });
    }
    return segments;
  }

  if (pathname.startsWith('/projects/')) {
    const id = pathname.slice('/projects/'.length).split('/')[0];
    const raw = customLabel || 'Project';
    const segments: BreadcrumbSegment[] = [
      { label: 'My Projects', path: '/projects' },
      { label: truncate(raw), path: `/projects/${id}`, title: raw },
    ];
    if (suffix) {
      segments.push({
        label: truncate(suffix),
        path: pathname + (location.search ?? ''),
        title: suffix,
      });
    }
    return segments;
  }

  if (isDeskPath(pathname)) {
    return [{ label: 'RCM Desk', path: DESK_PATH }];
  }

  if (isOpinionsPath(pathname)) {
    const segments: BreadcrumbSegment[] = [
      { label: 'RCM Opinions', path: OPINIONS_PATH },
    ];
    if (pathname !== OPINIONS_PATH) {
      const raw = customLabel || 'Article';
      segments.push({ label: truncate(raw), path: pathname, title: raw });
    }
    return segments;
  }

  if (pathname === '/admin' || pathname.startsWith('/admin/')) {
    const segments: BreadcrumbSegment[] = [{ label: 'Admin', path: '/admin' }];
    if (suffix) {
      segments.push({
        label: truncate(suffix),
        path: pathname + (location.search ?? ''),
        title: suffix,
      });
    }
    return segments;
  }

  return [];
}

interface ContentBreadcrumbsProps {
  defaultToolbar?: React.ReactNode;
}

export function ContentBreadcrumbs({ defaultToolbar }: ContentBreadcrumbsProps) {
  const breadcrumbs = useShellBreadcrumbs();
  const breadcrumbCtx = useContext(BreadcrumbContext);
  const toolbarContext = useContext(NavbarToolbarContext);
  const actions = breadcrumbCtx?.breadcrumbActions ?? null;
  const toolbar = toolbarContext?.toolbar ?? defaultToolbar ?? null;
  const navRef = useRef<HTMLElement>(null);
  const [compact, setCompact] = useState(true);

  useLayoutEffect(() => {
    const el = navRef.current;
    if (!el) return;

    const update = (width: number) => {
      const nextCompact = width < COMPACT_MAX_WIDTH;
      setCompact(nextCompact);
      breadcrumbCtx?.setHeaderCompact(nextCompact);
      breadcrumbCtx?.setHeaderInline(true);
    };

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) update(entry.contentRect.width);
    });

    observer.observe(el);
    update(el.getBoundingClientRect().width);

    return () => observer.disconnect();
  }, [breadcrumbCtx]);

  if (breadcrumbs.length === 0) return null;

  const breadcrumbList = (
    <ol className="flex min-w-0 items-center gap-1 text-sm leading-none">
      {breadcrumbs.map((seg, i) => {
        const isLast = i === breadcrumbs.length - 1;
        return (
          <li key={`${seg.path}-${i}`} className="flex min-w-0 items-center gap-1">
            {i > 0 && (
              <ChevronRightIcon
                className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground/70"
                aria-hidden
              />
            )}
            {isLast ? (
              <span
                className="truncate font-medium text-foreground"
                title={seg.title ?? seg.label}
              >
                {seg.label}
              </span>
            ) : (
              <Link
                to={seg.path}
                className="truncate font-medium text-muted-foreground transition-colors hover:text-foreground"
                title={seg.title ?? seg.label}
              >
                {seg.label}
              </Link>
            )}
          </li>
        );
      })}
    </ol>
  );

  const actionButtons = actions ? (
    <div className="flex flex-shrink-0 flex-nowrap items-center justify-end gap-1.5">
      {actions}
    </div>
  ) : null;

  return (
    <nav
      ref={navRef}
      className="flex-shrink-0 px-4 pb-4 pt-5 md:px-6"
      aria-label="Breadcrumb"
    >
      <div
        className={cn(
          'mx-2 flex min-h-9 items-center gap-2 border-b border-border/40 pb-3 md:mx-3 md:gap-3',
          !toolbar && 'justify-between',
        )}
      >
        <div className="min-w-0 flex-1 basis-0">{breadcrumbList}</div>
        {toolbar ? (
          <div
            className={cn(
              'min-w-0 shrink-0 relative z-10 transition-[width,max-width] duration-300 ease-out',
              compact
                ? 'w-[8.5rem] sm:w-[10.5rem] focus-within:w-[18rem] sm:focus-within:w-[20rem]'
                : 'w-[18rem] max-w-[18rem] focus-within:w-[22rem] focus-within:max-w-[22rem]',
            )}
          >
            {toolbar}
          </div>
        ) : null}
        {actionButtons}
      </div>
    </nav>
  );
}

/** @deprecated Use ContentBreadcrumbs */
export const BreadcrumbBar = ContentBreadcrumbs;
